import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { api } from "@/lib/api";
import { Calendar, FileText, FlaskConical, FolderOpen, Home, LayoutDashboard, LucideIcon, Microscope, Settings, Shield, Stethoscope, User, UserCog, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

interface MenuItem {
  id: number;
  nome: string;
  rota?: string;
  icone?: string;
  ordem: number;
  visivel: boolean;
}

// Mapeamento de ícones do banco para componentes do Lucide
const iconMap: Record<string, LucideIcon> = {
  home: Home,
  users: Users,
  calendar: Calendar,
  stethoscope: Stethoscope,
  microscope: Microscope,
  "user-cog": UserCog,
  shield: Shield,
  settings: Settings,
  "layout-dashboard": LayoutDashboard,
  "folder-open": FolderOpen,
  "flask-conical": FlaskConical,
  "file-text": FileText,
  user: User,
};

const MENU_DASHBOARD_PADRAO: MenuItem = {
  id: -1,
  nome: "Dashboard",
  rota: "/dashboard",
  icone: "layout-dashboard",
  ordem: -999,
  visivel: true,
};

export function AppSidebar() {
  const { user } = useAuth();
  const location = useLocation();
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { hasMenuAccess, loading: permissionsLoading } = usePermissions();

  useEffect(() => {
    const fetchMenus = async () => {
      try {
        setLoading(true);
        const response = await api.get("/api/menu_sistema");
        const menus = response.result || [];
        
        // Filtrar apenas menus visíveis e ordenar por ordem
        const visibleMenus = menus
          .filter((menu: MenuItem) => menu.visivel)
          .sort((a: MenuItem, b: MenuItem) => a.ordem - b.ordem);

        const hasDashboardMenu = visibleMenus.some((menu: MenuItem) => {
          const nome = (menu.nome || "").toLowerCase();
          const rota = (menu.rota || "").toLowerCase();
          return nome.includes("dashboard") || rota === "/dashboard";
        });

        setMenuItems(
          hasDashboardMenu
            ? visibleMenus
            : [MENU_DASHBOARD_PADRAO, ...visibleMenus]
        );
      } catch (error) {
        console.error("Erro ao carregar menus:", error);
        setMenuItems([MENU_DASHBOARD_PADRAO]);
      } finally {
        setLoading(false);
      }
    };

    fetchMenus();
  }, []);

  // Filtrar menus baseado nas permissões do usuário
  const filteredMenuItems = useMemo(() => {
    if (permissionsLoading) {
      return [];
    }

    return menuItems.filter((menu) => {
      if (menu.id < 0) return true;
      return hasMenuAccess(menu.id);
    });
  }, [menuItems, hasMenuAccess, permissionsLoading]);

  const menuItemsByProfile = useMemo(() => {
    const isPacienteRoute = location.pathname.startsWith("/paciente");
    const isPacienteUser =
      String(user?.id_tipo_usuario ?? "").toUpperCase() === "PACIENTE";

    if (isPacienteRoute || isPacienteUser) {
      return filteredMenuItems.map((item) =>
        item.id === MENU_DASHBOARD_PADRAO.id
          ? { ...item, rota: "/paciente/dashboard" }
          : item
      );
    }

    return filteredMenuItems;
  }, [filteredMenuItems, location.pathname, user?.id_tipo_usuario]);

  const getIcon = (iconName?: string): LucideIcon => {
    if (!iconName) return Home; // Ícone padrão
    return iconMap[iconName.toLowerCase()] || Home;
  };

  const resolveMenuRoute = (item: MenuItem): string => {
    const route = item.rota?.trim();
    const menuName = item.nome?.trim().toLowerCase();
    const routeNormalized = route?.toLowerCase();

    if (menuName?.includes("validar") && menuName?.includes("documento")) {
      if (!route || routeNormalized === "/usuarios") {
        return "/assinaturas/validar-documento";
      }

      if (routeNormalized === "/assinaturas/validar-documentos") {
        return "/assinaturas/validar-documento";
      }
    }

    return route || "#";
  };

  return (
    <Sidebar className="border-r border-sidebar-border">
      <SidebarHeader className="border-b border-sidebar-border p-4">
        <h2 className="text-xl font-bold text-sidebar-foreground">Instituto Elisadape</h2>
        <p className="text-xs text-sidebar-foreground/70">Sistema de Gestão Oftalmologia</p>
      </SidebarHeader>
      
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="text-sidebar-foreground/70">Menu Principal</SidebarGroupLabel>
          <SidebarGroupContent>
            {loading || permissionsLoading ? (
              <div className="p-4 text-center text-sm text-sidebar-foreground/70">
                Carregando menu...
              </div>
            ) : menuItemsByProfile.length === 0 ? (
              <div className="p-4 text-center text-sm text-sidebar-foreground/70">
                Nenhum menu disponível
              </div>
            ) : (
              <SidebarMenu>
                {menuItemsByProfile.map((item) => {
                  const IconComponent = getIcon(item.icone);
                  return (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton asChild>
                        <NavLink 
                          to={resolveMenuRoute(item)}
                          className={({ isActive }) =>
                            isActive 
                              ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium" 
                              : "text-sidebar-foreground hover:bg-sidebar-accent/50"
                          }
                        >
                          <IconComponent className="h-4 w-4" />
                          <span>{item.nome}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            )}
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
